import { accountingType } from '@/features/master-table/models';
import {
  actionOfReflectionStatus,
  inspectionByExternalExpertReason,
  opinionByAdministrativeReviewTeam,
  skipAdministrativeReformTeamEvaluationReason,
} from '@/features/project/projects/_models/project';
import { t } from '@/i18n';
import { reviewTypeEnum } from '@/utils/enum';
import { z } from 'zod';

/// ////////////////////////////////////////
/// Enum
/// ////////////////////////////////////////

export const reportFormatType = z.enum([
  'format1',
  'format2',
  'format3',
  'format4',
  'format5',
]);

export const ReportFormatLabelModifier = {
  format1: t('report-format1'),
  format2: t('report-format2'),
  format3: t('report-format3'),
  format4: t('report-format4'),
  format5: t('report-format5'),
} as const satisfies Record<z.infer<typeof reportFormatType>, string>;

export const ExportReportFormatLabelModifier = {
  format1: t('format1'),
  format2: t('format2'),
  format3: t('format3'),
  format4: t('format4'),
  format5: t('format5'),
} as const satisfies Record<z.infer<typeof reportFormatType>, string>;

export const format1ColumnType = z.enum([
  'policyLevel1',
  'policyLevel2',
  'projectNumber',
  'name',
  'parentOrganization',
  'startYear',
  'endYear',
  'accountTitle',
  'executableAmount',
  'executionAmount',
  'initialBudgetTotal',
  'posteriorRequestedAmountTotal',
  'differencePosteriorRequestedSelfInitialBudget',
  'externalOpinion',
  'adminReformReview',
  'reflectedAmount',
  'reflectedContents',
  'externalEvaluationScope',
  'externalEvaluationReason',
  'externalEvaluationLastExecutionYear',
]);

export const format2ColumnType = z.enum([
  'policyLevel1',
  'policyLevel2',
  'projectNumber',
  'name',
  'adminReformReviewOpinion',
  'posteriorRequestedAmountTotal',
  'organization',
  'accountingType',
]);

export const format3ColumnType = z.enum([
  'projectNumber',
  'name',
  'previousFinalizedAmountTotal',
  'previousExecutionAmountTotal',
  'resultOfPublishing',
  'finalizedAmount',
  'requestedAmount',
  'differenceBetweenRequestedAmountAndInitialBudget',
  'accountingTypeName',
  'reflectedAmount',
  'reflectionStatusAction',
  'reflectionStatusContext',
]);

export const format4ColumnType = z.enum([
  'jurisdiction',
  'generalSpecialPreviousYearProjectCount',
  'generalSpecialAbolitionProjectCount',
  'generalSpecialAbolitionProjectReflectedAmount',
  'generalSpecialReductionProjectCount',
  'generalSpecialReductionProjectReflectedAmount',
  'generalSpecialImprovementProjectCount',
  'generalPreviousYearProjectCount',
  'generalAbolitionProjectCount',
  'generalAbolitionProjectReflectedAmount',
  'generalReductionProjectCount',
  'generalReductionProjectReflectedAmount',
  'generalAbolitionReductionTotalProjectCount',
  'generalAbolitionReductionTotalProjectReflectedAmount',
  'generalImprovementProjectCount',
  'generalProjectRequestedAmount',
  'specialPreviousYearProjectCount',
  'specialAbolitionProjectCount',
  'specialAbolitionProjectReflectedAmount',
  'specialReductionProjectCount',
  'specialReductionProjectReflectedAmount',
  'specialAbolitionReductionTotalProjectCount',
  'specialAbolitionReductionTotalProjectReflectedAmount',
  'specialImprovementProjectCount',
  'specialProjectRequestedAmount',
]);

export const format5ColumnType = z.enum([
  'policyLevel1',
  'policyLevel2',
  'projectNumber',
  'name',
  'parentOrganization',
  'startYear',
  'endYear',
  'accountTitle',
  'majorExpenditureBudgetPurposeParent',
  'majorExpenditureBudgetPurpose',
  'executableAmount',
  'executionAmount',
  'initialBudgetTotal',
  'skipAdministrativeReformTeamEvaluationReason',
]);

/// ////////////////////////////////////////
/// Model
/// ////////////////////////////////////////

export const ReportOrganizingTableOrganizationsModel = z.object({
  organizations: z.object({ id: z.string().uuid(), name: z.string() }).array(),
});

const ReportOrganizingTableFormat1ProjectDataModel = z.object({
  name: z.string(),
  projectNumber: z.string(),
  startYear: z.number().nullable(),
  endYear: z.number().nullable(),
  previousFinalizedAmountTotal: z.number(), // 予算額計
  negativePreviousFinalizedAmountCount: z.number(), // 前年度予算額計のマイナス件数
  previousExecutionAmountTotal: z.number(), //  執行額
  negativePreviousYearExecutionAmountCount: z.number(), // 前年度執行額のマイナス件数
  initialBudgetTotal: z.number(), // 当年度当初予算額
  negativeInitialBudgetCount: z.number(), // 当年度当初予算額のマイナス件数
  posteriorRequestedAmountTotal: z.number(), // 翌年度要求額
  negativePosteriorRequestedAmountCount: z.number(), // 翌年度要求額のマイナス件数
  differencePosteriorRequestedSelfInitialBudget: z.number(), // 差引き
  externalOpinion: z.string().nullable(),
  adminReformReview: z.object({
    opinion: opinionByAdministrativeReviewTeam.nullable(), // 行政事業レビュー推進チームの所見
  }),
  reflectionStatus: z.object({
    reflectedAmount: z.number().nullable(), // 反映額
    reflectionContent: z.object({
      action: actionOfReflectionStatus.nullable(),
    }),
  }),
  parentOrganization: z.string(),
  externalEvaluationScope: reviewTypeEnum.nullable(),
  externalEvaluationReason: inspectionByExternalExpertReason.nullable(),
  externalEvaluationLastExecutionYear: z.number().nullable(),
  accountTitle: z.string().nullable(),
  accountingType,
});

export const ReportOrganizingTableFormat1PolicesModel = z.object({
  policyName: z.string().nullable(),
  policyCode: z.number().nullable(),
  policyMeasuresData: z
    .object({
      measureName: z.string().nullable(),
      measureCode: z.number().nullable(),
      projectsData: ReportOrganizingTableFormat1ProjectDataModel.array(),
    })
    .array(),
});

export const ReportOrganizingTableFormat1SummaryModel = z.object({
  accountTitle: z.string().nullable(),
  accountingType,
  skipAdministrativeReformTeamEvaluationPreviousReflectionStatusReflectedAmount:
    z.number(),
  skipAdministrativeReformTeamEvaluationPreviousExecutionAmountTotal:
    z.number(),
  skipAdministrativeReformTeamEvaluationInitialBudgetTotal: z.number(),
  skipAdministrativeReformTeamEvaluationPosteriorRequestedAmountTotal:
    z.number(),
  skipAdministrativeReformTeamEvaluationDifferencePosteriorRequestedSelfInitialBudget:
    z.number(),
  skipAdministrativeReformTeamEvaluationPreviousFinalizedAmountTotal:
    z.number(),
  administrativeReformTeamEvaluationPreviousReflectionStatusReflectedAmount:
    z.number(),
  administrativeReformTeamEvaluationPreviousExecutionAmountTotal: z.number(),
  administrativeReformTeamEvaluationInitialBudgetTotal: z.number(),
  administrativeReformTeamEvaluationPosteriorRequestedAmountTotal: z.number(),
  administrativeReformTeamEvaluationDifferencePosteriorRequestedSelfInitialBudget:
    z.number(),
  administrativeReformTeamEvaluationPreviousFinalizedAmountTotal: z.number(),
  comprehensivePreviousExecutionAmountTotal: z.number(),
  comprehensivePreviousReflectionStatusReflectedAmount: z.number(),
  comprehensivePreviousInitialBudgetTotal: z.number(),
  comprehensivePosteriorRequestedAmountTotal: z.number(),
  comprehensiveDifferencePosteriorRequestedSelfInitialBudget: z.number(),
  comprehensivePreviousFinalizedAmountTotal: z.number(),
});

export const ReportOrganizingTableFormat1Model =
  ReportOrganizingTableOrganizationsModel.extend({
    policies: ReportOrganizingTableFormat1PolicesModel.array(),
    summary: z.object({
      general: ReportOrganizingTableFormat1SummaryModel.array(),
      special: ReportOrganizingTableFormat1SummaryModel.array(),
    }),
  });

const Format2ProjectDataModel = z.object({
  name: z.string().nullable(),
  projectNumber: z.string().nullable(),
  adminReformReviewOpinion: opinionByAdministrativeReviewTeam.nullable(),
  posteriorRequestedAmountTotal: z.number().nullable(),
  negativeRequestedAmountCount: z.number(),
  organization: z.string().nullable(),
  accountingType: accountingType.nullable(),
  accountTitle: z.string().nullable(),
});

export const ReportOrganizingTableFormat2PolicesModel = z.object({
  policyName: z.string().nullable(),
  policyCode: z.number().nullable(),
  policyMeasuresData: z
    .object({
      measureName: z.string().nullable(),
      measureCode: z.number().nullable(),
      projectsData: Format2ProjectDataModel.array(),
    })
    .array(),
});

export const ReportOrganizingTableFormat2Model =
  ReportOrganizingTableOrganizationsModel.extend({
    policies: ReportOrganizingTableFormat2PolicesModel.array(),
    summary: z.object({
      generalAccountingTypeTotal: z.number().nullable(),
      specialAccountingType: z
        .object({
          accountTitle: z.string(),
          total: z.number(),
        })
        .array(),
    }),
  });

export const ReportOrganizingTableFormat3ProjectModel = z.object({
  name: z.string(),
  projectNumber: z.string(),
  accountTitleId: z.string().uuid().nullable(),
  accountTitleName: z.string().nullable(), // 勘定科目
  resultOfPublishing: z.string().nullable(), // 公開プロセス結果概要
  finalizedAmount: z.number(), // 予算額
  negativeFinalizedAmountCount: z.number(),
  reflectedAmount: z.number(), // 反映額
  negativeReflectedAmountCount: z.number(),
  requestedAmount: z.number(), // 要求額
  negativeRequestedAmountCount: z.number(),
  accountingTypeId: z.string().uuid().nullable(),
  accountingTypeType: accountingType.nullable(),
  accountingTypeName: z.string().nullable(), // 会計区分
  reflectionStatusAction: actionOfReflectionStatus.nullable(),
  reflectionStatusContext: z.string().nullable(), // 詳細
  previousExecutionAmountTotal: z.number(), // 前年執行
  negativePreviousExecutionAmountTotalCount: z.number(),
  previousFinalizedAmountTotal: z.number(), // 前年予算額
  negativePreviousFinalizedAmountTotalCount: z.number(),
  differenceBetweenRequestedAmountAndInitialBudget: z.number(), // 当年度差引き
});

export const ReportOrganizingTableFormat3Model =
  ReportOrganizingTableOrganizationsModel.extend({
    projects: ReportOrganizingTableFormat3ProjectModel.array(),
    summary: z.object({
      finalizedAmount: z.number(),
      reflectedAmount: z.number(),
      requestedAmount: z.number(),
      previousExecutionAmountTotal: z.number(),
      previousFinalizedAmountTotal: z.number(),
      differenceBetweenRequestedAmountAndInitialBudget: z.number(),
    }),
  });

export const ReportOrganizingTableFormat4SummaryDataModel = z.object({
  previousYearProjectCount: z.number(),
  abolitionProjectCount: z.number(),
  abolitionProjectReflectedAmount: z.number(),
  reductionProjectCount: z.number(),
  reductionProjectReflectedAmount: z.number(),
  abolitionReductionProjectCount: z.number(),
  abolitionReductionProjectReflectedAmount: z.number(),
  improvementProjectCount: z.number(),
  projectRequestedAmount: z.number(),
});

export const ReportOrganizingTableFormat4SummaryByAccountModel = z.object({
  generalSpecial: ReportOrganizingTableFormat4SummaryDataModel.omit({
    abolitionReductionProjectCount: true,
    abolitionReductionProjectReflectedAmount: true,
    projectRequestedAmount: true,
  }),
  general: ReportOrganizingTableFormat4SummaryDataModel,
  special: ReportOrganizingTableFormat4SummaryDataModel,
});

const ReportOrganizingTableFormat4OrganizationModel = z.object({
  id: z.string().uuid(),
  name: z.string(),
  summaryByAccount: ReportOrganizingTableFormat4SummaryByAccountModel,
});

export const ReportOrganizingTableFormat4Model = z.object({
  organizations: ReportOrganizingTableFormat4OrganizationModel.array(),
  totalSummaryByAccount: ReportOrganizingTableFormat4SummaryByAccountModel,
});

const ReportOrganizingTableFormat5ProjectDataModel = z.object({
  projectNumber: z.string(),
  name: z.string(),
  parentOrganization: z.string(),
  startYear: z.number().nullable(),
  endYear: z.number().nullable(),
  accountTitle: z.string().nullable(),
  accountingType,
  majorExpenditureBudgetPurposeParent: z.string(),
  majorExpenditureBudgetPurpose: z.string(),
  previousFinalizedAmountTotal: z.number(),
  negativePreviousFinalizedAmountCount: z.number(), // 前年度予算額計のマイナス件数
  previousExecutionAmountTotal: z.number(),
  negativePreviousYearExecutionAmountCount: z.number(), // 前年度執行額のマイナス件数
  initialBudgetTotal: z.number(),
  negativeInitialBudgetCount: z.number(), // 当年度当初予算額のマイナス件数
  skipAdministrativeReformTeamEvaluationReason,
});

export const ReportOrganizingTableFormat5PolicesModel = z.object({
  policyName: z.string().nullable(),
  policyCode: z.number().nullable(),
  policyMeasuresData: z
    .object({
      measureName: z.string().nullable(),
      measureCode: z.number().nullable(),
      projectsData: ReportOrganizingTableFormat5ProjectDataModel.array(),
    })
    .array(),
});

export const ReportOrganizingTableFormat5SummaryModel = z.object({
  accountTitle: z.string().nullable(),
  accountingType,
  skipAdministrativeReformTeamEvaluationInitialBudgetTotal: z.number(),
  skipAdministrativeReformTeamEvaluationPreviousExecutionAmountTotal:
    z.number(),
  skipAdministrativeReformTeamEvaluationPreviousFinalizedAmountTotal:
    z.number(),
});

export const ReportOrganizingTableFormat5Model = z.object({
  organizations: z.object({ id: z.string().uuid(), name: z.string() }).array(),
  policies: ReportOrganizingTableFormat5PolicesModel.array(),
  summary: z.object({
    general: ReportOrganizingTableFormat5SummaryModel.array(),
    special: ReportOrganizingTableFormat5SummaryModel.array(),
  }),
});

// /// ////////////////////////////////////////
// /// Schema
// /// ////////////////////////////////////////

export const ReportOrganizingTableFilterSchema = z.object({
  fiscalYear: z.any().transform((v) => Number(v)),
  organizationIds: z.string().uuid().array().min(1),
  formatNumber: reportFormatType,
});
